/**
 * eGO API Client Module
 *
 * Provides functions for making HTTP requests to the eGO device management API.
 * Follows the same patterns as the site API client.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6, 8.7, 8.8
 */

import type { EgoDevice, EgoDeviceConfig, UpsertEgoConfigRequest } from '../models/egoModels';

/** Configuration for the eGO API client */
export interface EgoApiClientConfig {
  readonly apiHost: string;
  readonly getAccessToken: () => Promise<string | null>;
}

/** Error thrown when the eGO API returns a non-OK response */
export class EgoApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'EgoApiError';
  }
}

/**
 * Fetches all eGO devices.
 * GET {apiHost}/admin-api/ego/configs
 *
 * Requirements: 8.2, 8.5, 8.6, 8.7
 */
export async function fetchEgoDevices(
  config: EgoApiClientConfig
): Promise<readonly EgoDevice[]> {
  const token = await config.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${config.apiHost}/admin-api/ego/configs`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new EgoApiError(response.status, `API error: ${response.status}`);
  }

  const data: { readonly configs: readonly EgoDevice[] } = await response.json();
  return data.configs;
}

/**
 * Result of fetching a single eGO device config */
export interface EgoDeviceConfigResult {
  readonly config: EgoDeviceConfig;
  readonly lastUpdated: string;
}

/**
 * Fetches a single eGO device config by deviceId.
 * GET {apiHost}/ego/{deviceId}/config
 * Returns null on 404 (unknown device).
 *
 * Requirements: 8.3, 8.5, 8.6, 8.7, 8.8
 */
export async function fetchEgoDeviceConfig(
  deviceId: string,
  config: EgoApiClientConfig
): Promise<EgoDeviceConfigResult | null> {
  const token = await config.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(
    `${config.apiHost}/ego/${deviceId}/config`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new EgoApiError(response.status, `API error: ${response.status}`);
  }

  const device: EgoDevice = await response.json();
  return { config: device.config, lastUpdated: device.lastUpdated };
}

/**
 * Upserts an eGO device config.
 * PUT {apiHost}/admin-api/ego/{deviceId}/config
 *
 * Requirements: 8.4, 8.5, 8.7
 */
export async function upsertEgoDeviceConfig(
  deviceId: string,
  body: UpsertEgoConfigRequest,
  config: EgoApiClientConfig
): Promise<void> {
  const token = await config.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(
    `${config.apiHost}/admin-api/ego/${deviceId}/config`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    throw new EgoApiError(response.status, `API error: ${response.status}`);
  }
}
